/**
 * Solide généré (cube, pavé, pyramide, cône, cylindre, boule) dans la vidéo : le même solide que dans l'app, qui tourne
 * lentement ; ses dimensions suivent les curseurs de l'étape (glissement), le volume s'affiche et se met à jour.
 */
import { ThreeCanvas } from "@remotion/three";
import { useThree } from "@react-three/fiber";
import { useLayoutEffect, useMemo } from "react";
import { FPS, paramsAt, SOLID_SHAPES, solidDims, solidVolume, type FigureState, type ModelState, type SolidSpec } from "@jange/shared";
import { SolidMesh, solidExtent } from "@jange/lesson-player/solid";
import type { Theme } from "../styles.js";

const TURN = 40 * FPS; // un tour en 40 s
const clamp01 = (t: number) => Math.max(0, Math.min(1, t));
const easeOut = (t: number) => 1 - Math.pow(1 - t, 3);
const nf = (v: number) => (Math.round(v * 10) / 10).toString().replace(".", ",");

function LookAt({ y }: { y: number }) {
  const { camera } = useThree();
  useLayoutEffect(() => { camera.lookAt(0, y, 0); camera.updateProjectionMatrix(); }, [camera, y]);
  return null;
}

export function Solid({ spec, model, figure, frame, width, height, theme }: { spec: SolidSpec; model: ModelState; figure?: FigureState; frame: number; width: number; height: number; theme: Theme }) {
  const shown = model.shownAt ?? frame;
  const appear = model.shownAt === null ? 0 : easeOut(clamp01((frame - shown) / (0.5 * FPS)));
  const params = figure ? paramsAt(figure, frame) : Object.fromEntries(spec.sliders.map((s) => [s.name, s.initial]));
  const d = solidDims(spec, params);
  const extent = useMemo(() => solidExtent(spec), [spec]);
  const camera = useMemo(() => ({ position: [extent * 1.5, extent * 1.1, extent * 1.9] as [number, number, number], fov: 40, near: 0.1, far: extent * 20 }), [extent]);
  const angle = ((frame - shown) * 2 * Math.PI) / TURN;
  const info = SOLID_SHAPES[spec.shape];
  const volume = solidVolume(spec.shape, d);
  const onWhite = !theme.rough;

  return (
    <div style={{ position: "relative", width, height, borderRadius: theme.rough ? 0 : 22, overflow: "hidden", background: onWhite ? "#F6F7F9" : "rgba(0,0,0,0.18)", opacity: appear }}>
      <ThreeCanvas width={width} height={height} dpr={1} gl={{ antialias: true }} camera={camera}>
        <LookAt y={extent / 3} />
        <ambientLight intensity={0.75} />
        <directionalLight position={[5, 8, 6]} intensity={1.1} />
        <group rotation={[0, angle, 0]} scale={0.94 + 0.06 * appear}>
          <SolidMesh spec={spec} d={d} />
        </group>
        <gridHelper args={[extent * 2.2, 8, onWhite ? "#C9CFDA" : "#5F7A70", onWhite ? "#E3E6EC" : "#3E5A50"]} />
      </ThreeCanvas>
      <div style={{ position: "absolute", left: 18, top: 14, fontFamily: theme.fontFamily, color: onWhite ? "#15213B" : theme.text, fontSize: 30, fontWeight: 800 }}>{info.label}</div>
      {spec.show.volume && (
        <div style={{ position: "absolute", left: 18, bottom: 16, fontFamily: theme.fontFamily, background: onWhite ? "#FFFFFF" : "rgba(8,20,24,0.7)", color: onWhite ? "#15213B" : theme.text, borderRadius: 14, padding: "8px 14px", fontSize: 26, fontWeight: 700 }}>
          V = {info.volume} = <span style={{ color: onWhite ? "#0E6B66" : theme.amber }}>{nf(volume)} {spec.unit}³</span>
        </div>
      )}
    </div>
  );
}
